
import { defineComponent } from 'vue'
import { format, parseISO, differenceInDays, eachMonthOfInterval, subMonths, endOfMonth, startOfMonth, addMinutes } from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'
import { Button, PrimaryButton, Select, Form, Table, Input } from '@design-system/ui'
import { faPaperclip, faPaperPlane, faSearch, faTimesCircle, faTrash, faFileAlt, faMailBulk, faCircleCheck, faMoneyBill } from '@fortawesome/free-solid-svg-icons'
import SessionService from '../SessionService'
import Accordion from '@/components/Accordion.vue'
import Toast from '@/components/Toast.vue'
import api from '@/services/api'
import utils from '@/utils'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { email } from 'validacao-docs'
import { ListSoftwareHousesDTO } from '@/interfaces/ListSoftwareHousesDTO'
import CustomInputAutocomplete from '../components/input/InputAutoComplete.vue'
import CustomInput from '../components/input/Input.vue'
import CustomTextArea from '../components/input/TextArea.vue'

export default defineComponent({
  name: 'Comissoes',
  components: {
    Table,
    PrimaryButton,
    Input,
    Form,
    CustomInputAutocomplete,
    CustomInput,
    Accordion,
    CustomTextArea,
    Button,
    FontAwesomeIcon,
    Select,
    Toast
  },
  data () {
    return {
      errorMessage: null,
      months: [],
      selectedMonth: {
        value: '',
        key: ''
      },
      selectedMonthTotal: 'R$ 0,00',
      toastSuccess: true,
      message: '',
      hidden: true,
      timeSeen: 20000,
      commissionRequests: [],
      buttonIcons: {
        search: {
          icon: faSearch
        },
        sendEmail: {
          icon: faPaperPlane
        },
        cancel: {
          icon: faTimesCircle
        },
        trash: {
          icon: faTrash
        },
        upload: {
          icon: faPaperclip
        },
        file: {
          icon: faFileAlt
        },
        message: {
          icon: faMailBulk
        },
        done: {
          icon: faCircleCheck
        },
        commission: {
          icon: faMoneyBill
        }
      },
      commissionLoading: false,
      firstLoading: true,
      SOFTWARE_HOUSE_CNPJ: null,
      downloadInProgress: false,
      ptBRDate: ptBR,
      softwareHouse: {},
      startFilter: '',
      isRoot: false,
      allSoftwareHouses: [],
      loadingSoftwareHouses: false,
      softwareHouseSelected: null,
      softwareHouses: [],
      isSoftwareHouseIncorrect: false,
      searchSoftwareHouseText: '',
      endFilter: '',
      commissionModal: false,
      commissionForm: {
        to: 'compras@tecnospeed.com.br',
        from: null,
        subject: null,
        message: null,
        error: false,
        errorMessage: '',
        maxFileSize: 3,
        types: "['image/png', 'image/jpg', 'image/jpeg', 'application/pdf']",
        total: 0,
        files: [],
        validations: {
          from: ''
        },
        sent: false,
        prototocolNumber: '',
        complement: ''
      },
      protocolStatus: {
        pending: 'Aguardando',
        rejected: 'Rejeitado',
        scheduled: 'Agendado',
        finished: 'Finalizado'
      },
      selectedProtocolStatus: {
        value: 'Todos',
        key: ''
      },
      protocolLoading: false,
      protocolStatusOptions: [
        {
          value: 'Todos',
          key: ''
        },
        {
          value: 'Aguardando',
          key: 'pending'
        },
        {
          value: 'Agendado',
          key: 'scheduled'
        },
        {
          value: 'Rejeitado',
          key: 'rejected'
        },
        {
          value: 'Finalizado',
          key: 'finished'
        }
      ]
    }
  },
  async mounted () {
    const session = new SessionService()
    const token = localStorage.getItem('token')

    const actualMonth = new Date().getMonth()
    this.currentMonth = actualMonth
    this.firstLoading = true

    const monthsInterval = eachMonthOfInterval({
      start: subMonths(new Date(), 13),
      end: subMonths(new Date(), 1)
    })

    for (const month of monthsInterval) {
      this.months.push({
        key: month,
        value: format(month, 'MM/yyyy')
      })
    }

    this.selectedMonth = this.months[this.months.length - 1]

    const dateNow = new Date()
    this.startFilter = format(subMonths(dateNow, 1), 'yyyy-MM-dd')
    this.endFilter = format(dateNow, 'yyyy-MM-dd')

    if (session.rootAccess()) {
      this.isRoot = true
      this.listSoftwareHouses()

      return
    }

    this.SOFTWARE_HOUSE_CNPJ = session.cnpj()
    this.softwareHouse = await session.softwareHouseData(token, this.SOFTWARE_HOUSE_CNPJ)
    this.cnpjSoftwareHouse = this.SOFTWARE_HOUSE_CNPJ

    this.getCommissionRequests()
    this.getTotalReceivableByMonth()
  },
  methods: {
    getCommissionRequests (value) {
      if (value) {
        this.updateSearchSoftwareHouseText(value.primaryText)
      }
      this.protocolLoading = true

      const rangeValidated = this.validateDateRange(this.startFilter, this.endFilter)
      if (!rangeValidated) {
        this.protocolLoading = false
        return
      }

      const queries = {
        dateStart: `${this.startFilter}T00:00:00.000Z`,
        dateFinish: `${this.endFilter}T23:59:59.999Z`,
        status: this.selectedProtocolStatus.key === '' ? undefined : this.selectedProtocolStatus.key,
        softwareHouseCnpj: this.softwareHouseSelected ? this.softwareHouseSelected.id : this.SOFTWARE_HOUSE_CNPJ
      }

      api.get('/commission-request', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: queries
      })
        .then(response => {
          const commissionRequestsReturned = response.data

          this.commissionRequests = commissionRequestsReturned.reduce((commissionsRequests, commissionRequest) => {
            if (commissionRequest.status === 'approved') {
              const currentDate = new Date()
              const currentDateAdjusted = addMinutes(currentDate, currentDate.getTimezoneOffset())
              const receiptDate = new Date(commissionRequest.receiptDate)
              const receiptDateAdjusted = addMinutes(receiptDate, receiptDate.getTimezoneOffset())

              if (receiptDateAdjusted < currentDateAdjusted) {
                commissionRequest.status = 'finished'
              } else {
                commissionRequest.status = 'scheduled'
                commissionRequest.message = `Previsão de pagamento: ${format(receiptDateAdjusted, 'dd/MM/yyyy')}`
              }
            }

            if (this.selectedProtocolStatus.key === '' || this.selectedProtocolStatus.key === commissionRequest.status) {
              commissionsRequests.push(commissionRequest)
            }

            return commissionsRequests
          }, [])

          setTimeout(() => {
            var evt = new CustomEvent('list')
            window.dispatchEvent(evt)
          }, 1)

          this.protocolLoading = false
        })
        .catch(() => {
          this.protocolLoading = false
        })
    },
    getTotalReceivableByMonth (value) {
      if (value) {
        this.updateSearchSoftwareHouseText(value.primaryText)
      }
      this.commissionLoading = true
      this.selectedMonthTotal = 'Carregando...'

      const rangeValidated = this.validateDateRange(this.startFilter, this.endFilter)
      if (!rangeValidated) {
        this.commissionLoading = false
        return
      }

      const queries = {
        start: format(startOfMonth(this.selectedMonth.key), 'yyyy-MM-dd'),
        end: format(endOfMonth(this.selectedMonth.key), 'yyyy-MM-dd')
      }

      let total = 0
      api.get(`/volumetry/${this.softwareHouseSelected ? this.softwareHouseSelected.id : this.SOFTWARE_HOUSE_CNPJ}/summary`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: queries
      })
        .then(response => {
          response.data.forEach(({ volumetria }) => {
            volumetria.forEach(({ valorTotalTaxaCompany, valorTotalTaxaSh }) => {
              total += valorTotalTaxaCompany - valorTotalTaxaSh
            })
          })

          this.commissionForm.total = total
          this.selectedMonthTotal = `R$ ${total.toFixed(2).replace('.', ',')}`

          setTimeout(() => {
            var evt = new CustomEvent('list')
            window.dispatchEvent(evt)
          }, 1)

          this.commissionLoading = false
        })
        .catch(() => {
          this.commissionLoading = false
          this.selectedMonthTotal = `R$ ${total.toFixed(2).replace('.', ',')}`
        })
    },
    async listSoftwareHouses () {
      this.loadingSoftwareHouses = true
      api.get<ListSoftwareHousesDTO[]>('/software-houses', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(response => {
          this.allSoftwareHouses = response.data
          this.softwareHouses = this.allSoftwareHouses

          this.loadingSoftwareHouses = false

          setTimeout(() => {
            var evt = new CustomEvent('list')
            window.dispatchEvent(evt)
          }, 1)
        })
        .catch(() => {
          this.loadingSoftwareHouses = false
        })
    },

    handleMonth (selected) {
      this.selectedMonth = selected
      this.getTotalReceivableByMonth()
    },
    handleProtocolStatus (selected) {
      this.selectedProtocolStatus = selected
    },
    formatCnpj (cnpj: string): string {
      const cnpjFormatado = `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
      return cnpjFormatado
    },
    validateDateRange (startDateStr: string, endDateStr: string) {
      const intervalDays = differenceInDays(new Date(endDateStr), new Date(startDateStr))
      const maxInterval = 90

      if (new Date(endDateStr) > new Date()) {
        this.errorMessage = 'Não é possível buscar dados futuros'
        return false
      }

      if (intervalDays > maxInterval) {
        this.errorMessage = 'Intervalo de datas maior que 90 dias'
        return false
      }

      this.errorMessage = ''
      return true
    },
    handleCommissionModal () {
      const startDate = format(startOfMonth(this.selectedMonth.key), 'yyyy-MM-dd')
      const endDate = format(endOfMonth(this.selectedMonth.key), 'yyyy-MM-dd')
      const queries = {
        startDate: `${startDate}T00:00:00.000Z`,
        endDate: `${endDate}T23:59:59.999Z`
      }
      api.get(`/commission-request/${this.softwareHouseSelected ? this.softwareHouseSelected.id : this.SOFTWARE_HOUSE_CNPJ}/not-rejected`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: queries
      })
        .then(response => {
          const commissionRequest = response.data
          this.hidden = false
          this.toastSuccess = false
          this.message = 'Solicitação de repasse já existe'
          if (commissionRequest.status === 'pending') {
            this.message = 'Existe uma solicitação de repasse de comissão pendente para o período informado'
          }
          if (commissionRequest.status === 'finished') {
            this.message = 'Existe uma solicitação de repasse de comissão finalizada para o período informado'
          }
          setTimeout(() => {
            this.hidden = true
          }, this.timeSeen)
        }).catch(() => {
          this.commissionForm.from = null
          this.resetFile()
          this.commissionForm.files = []
          this.setDefaultMessage()
          this.commissionForm.requestProgress = 0

          this.commissionModal = true
        })
    },
    setDefaultMessage () {
      const startDate = format(startOfMonth(this.selectedMonth.key), 'dd/MM/yyyy')
      const endDate = format(endOfMonth(this.selectedMonth.key), 'dd/MM/yyyy')
      this.commissionForm.subject =
      `Solicitação de Repasse de comissões - ${
        utils.applyMask('CNPJ', this.cnpjSoftwareHouse)} - ${
          this.softwareHouse?.person?.juridicPerson?.legalName} de ${startDate} até ${endDate}`

      this.commissionForm.message =
      'Olá, tudo bem?\n\n' +
      `Gostaria de solicitar o repasse de comissões da empresa ${utils.applyMask('CNPJ', this.cnpjSoftwareHouse)} - ${this.softwareHouse?.person?.juridicPerson?.legalName}, ` +
      `referente ao período de ${startDate} até ${endDate}, totalizando o valor de R$ ${this.commissionForm.total.toFixed(2).replace('.', ',')}.\n` +
      'Segue a Nota Fiscal em anexo.'
    },
    async sendCommissionRequest () {
      if (this.commissionValidate()) {
        this.commissionForm.errorMessage = ''
        const token = localStorage.getItem('token')
        const startDate = format(startOfMonth(this.selectedMonth.key), 'yyyy-MM-dd')
        const endDate = format(endOfMonth(this.selectedMonth.key), 'yyyy-MM-dd')

        const data = new FormData()

        for (const file of this.commissionForm.files) {
          data.append('files', file)
        }
        data.append('requesterEmail', this.commissionForm.from)
        data.append('subject', `${this.commissionForm.subject}`)
        data.append('message', this.commissionForm.message)
        data.append('startDate', `${startDate}T00:00:00.000Z`)
        data.append('endDate', `${endDate}T23:59:59.999Z`)
        data.append('value', this.commissionForm.total)
        data.append('softwareHouseCnpj', this.softwareHouseSelected ? this.softwareHouseSelected.id : this.SOFTWARE_HOUSE_CNPJ)

        if (this.commissionForm.complement) {
          data.append('complement', this.commissionForm.complement)
        }

        api.post('/commission-request',
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            timeout: 0
          }
        ).then((response) => {
          const commissionRequestData = response.data
          this.hidden = false
          this.toastSuccess = true
          this.message = 'Solicitação de repasse de comissões enviada com sucesso!'
          this.commissionModal = false

          const commissionRequestCreatedAt = format(parseISO(commissionRequestData.createdAt), 'yyyy-MM-dd')
          if (commissionRequestCreatedAt >= this.startFilter && commissionRequestCreatedAt <= this.endFilter) {
            this.getCommissionRequests()
          }
          setTimeout(() => {
            this.hidden = true
          }, this.timeSeen)
        }).catch((error) => {
          this.hidden = false
          this.toastSuccess = false
          this.message = error.response && error.response.data && error.response.data.error_messages[0] ? error.response.data.error_messages[0] : 'Ocorreu um erro ao solicitar o repasse de comissão'
          setTimeout(() => {
            this.hidden = true
          }, this.timeSeen)
        })
      }
    },
    changeField (index: string, value: string): void {
      this.commissionForm[index] = value
    },
    commissionValidate () {
      let isValid = true

      if (!this.commissionForm.from || this.commissionForm.from === '') {
        this.commissionForm.validations.from = 'Informe um e-mail'
        isValid = false
      } else if (!email.isValidMask(this.commissionForm.from)) {
        this.commissionForm.validations.from = 'Informe um e-mail válido'
        isValid = false
      } else if (this.commissionForm.from.length > 100) {
        this.commissionForm.validations.from = 'O e-mail deve ter no máximo 150 caracteres.'
        isValid = false
      } else {
        this.commissionForm.validations.from = ''
      }

      return isValid
    },
    resetFile () {
      this.$refs.imageUploader.value = ''
    },
    fileChangeHandler (e: Event) {
      this.commissionForm.errorMessage = ''
      this.commissionForm.error = false

      const target = e.target as HTMLInputElement

      if (this.commissionForm.files.length + target.files.length > 5) {
        this.commissionForm.error = true
        this.commissionForm.errorMessage = 'No máximo 5 arquivos podem ser enviados.'
        return
      }

      Array.from(target.files).map((file) => {
        if (file.size > this.commissionForm.maxFileSize * 1024 * 1024) {
          this.commissionForm.error = true
          this.commissionForm.errorMessage = `O arquivo é maior que ${this.commissionForm.maxFileSize} MB`
        }
      })

      const files = Array.from(target.files)

      const fileCommission = files[0]

      if (!this.commissionForm.types.includes(fileCommission.type)) {
        this.commissionForm.error = true
        this.commissionForm.errorMessage = 'O arquivo enviado é inválido.'
      }
      if (!this.commissionForm.error) {
        this.commissionForm.files.push(fileCommission)
      }
      // this.showRemovedBtn = true
    },
    removeFileCommission (index: number) {
      this.commissionForm.errorMessage = ''
      this.commissionForm.error = false
      this.commissionForm.files = this.commissionForm.files.filter((_file: File, idx: number) => idx !== index)
    },
    hide () {
      this.hidden = true
    },
    searchSoftwareHouses () {
      let softwareHouses = this.allSoftwareHouses

      if (!this.searchSoftwareHouseText) {
        return softwareHouses
      }

      if (this.searchSoftwareHouseText) {
        const softwareHouseCnpj = this.searchSoftwareHouseText.replaceAll('/', '')
          .replaceAll('.', '')
          .replaceAll('-', '')
        softwareHouses = softwareHouses.filter((softwareHouse) => {
          const cnpjWithoutMask = softwareHouse.cnpj.replaceAll('/', '')
            .replaceAll('.', '')
            .replaceAll('-', '')
          return softwareHouse.razaoSocial.toLowerCase().includes(this.searchSoftwareHouseText) ||
            softwareHouse.nomeFantasia.toLowerCase().includes(this.searchSoftwareHouseText.toLowerCase()) ||
            cnpjWithoutMask.includes(softwareHouseCnpj)
        })
      }

      return softwareHouses
    },
    async selectSoftwareHouse (value) {
      const session = new SessionService()
      const token = localStorage.getItem('token')

      try {
        this.softwareHouse = await session.softwareHouseData(token, value.id)
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.softwareHouseSelected = null
          this.hidden = false
          this.toastSuccess = false
          this.message = 'Software house não está cadastrada no SHOP!'
          setTimeout(() => {
            this.hidden = true
          }, this.timeSeen)
          return
        }
      }

      this.softwareHouseSelected = value
      this.cnpjSoftwareHouse = value.id
      await this.getCommissionRequests(value)
      await this.getTotalReceivableByMonth(value)
      this.setDefaultMessage()
    },
    handleEdit (protocol) {
      const commissionRequest = this.commissionRequests.find(commissionRequest => commissionRequest.protocol === protocol)
      this.$router.push(`/comissoes/${commissionRequest.softwareHouse.cnpj}/${protocol}`)
    },
    updateSearchSoftwareHouseText (value) {
      this.searchSoftwareHouseText = value
      this.softwareHouses = this.searchSoftwareHouses()
    },
    downloadCommissionRequestFiles (protocol) {
      if (this.downloadInProgress) {
        return
      }
      this.downloadInProgress = true
      const token = localStorage.getItem('token')
      const softwareHouseCnpj = this.softwareHouseSelected ? this.softwareHouseSelected.id : this.SOFTWARE_HOUSE_CNPJ
      api.get(`/commission-request/files/${softwareHouseCnpj}/${protocol}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      })
        .then(response => {
          this.downloadInProgress = false
          const url = window.URL.createObjectURL(new Blob([response.data]))

          const link = document.createElement('a')
          link.href = url

          link.setAttribute('download', `${softwareHouseCnpj}-${protocol}.zip`)

          document.body.appendChild(link)
          link.click()

          document.body.removeChild(link)
        })
        .catch(() => {
          this.downloadInProgress = false
          this.hidden = false
          this.toastSuccess = false
          this.message = 'Erro ao baixar os arquivos'
          setTimeout(() => {
            this.hidden = true
          }, this.timeSeen)
        })
    },
    formatDate: format,
    parseISODate: parseISO
  },
  beforeMount () {
    this.$store.commit('changeActiveMenu', 4)
  }
})
